/**
 * The travel runtime's private state (plan R17): everything the `travel` subsystem keeps beside the frozen
 * `core.travel` and `core.nav` objects (which the HUD, audio, VFX and maps read). Built once per subsystem; the
 * per-system parts (mass-lock input, lane scratch) are rebuilt on system entry. Per-tick code only mutates it.
 */
import type { Quat, Vec3 } from '../../core/math'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { SpawnShipSpec } from '../../reach/runtime/types'
import type { TravelConfig } from '../config'
import {
  createDiscCrossing,
  createMassLockHit,
  createTransitGate,
  createTug,
  type DiscCrossingOut,
  type MassLockHit,
  type MassLockInput,
  type TransitGateOut,
  type TugState,
} from '../math'
import type { TravelRuntimeConfig } from './config'
import { createCruiseTarget, createDetachedShip, type CruiseTarget, type DetachedShip } from './kinematics'

/** `REV.cruiseDrop` reasons (the event's b). */
export const DROP = { auto: 0, manual: 1, emergency: 2, interdicted: 3 } as const
/** `REV.cruiseCharge` a: the charge started or was cancelled. */
export const CHARGE = { start: 0, cancel: 1 } as const
/** `REV.interdictEnd` b (`jettisoned`, M8 TR-8b1: the pilot dropped cargo to end the tug, M6 spec §6.4.2). */
export const INTERDICT_END = { escaped: 0, failed: 1, jettisoned: 2 } as const
/** `REV.transitEnd` b (additive, R17): 0 arrived at the destination, 1 the switch failed and the ship stayed home. */
export const TRANSIT_END = { arrived: 0, failed: 1 } as const

/** The live configs the runtime reads (the core's objects when it has them, so the dev panels edit one copy). */
export interface TravelConfigs {
  travel: TravelConfig
  runtime: TravelRuntimeConfig
  /** `reach-streaming.json` `transientDespawnM` (interdictor groups despawn beyond it). */
  streaming: { transientDespawnM: number }
}

export interface TransitRun {
  /** The relay being crossed (its id is the same in both systems). */
  relayId: string
  /** The system the jump started in (a failed switch comes back to it). */
  originId: string
  /** Bumped per transit, so a stale switch promise never touches a later one. */
  serial: number
  /** `transit.t` when the destination became ready. */
  readyAt: number
  /** The switch to the destination was rejected; the fallback re-enters the origin. */
  failed: boolean
  fallbackStarted: boolean
  /** The tunnel ran past `tunnelMaxS` (logged once as a budget miss). */
  overrun: boolean
}

/**
 * Scratch attitude and direction in their own hidden classes: V8 boxes every float stored into plain `{ x, y, z }` and
 * `{ x, y, z, w }` literals (their shared maps have generalised field representations), which would allocate per tick.
 */
class Attitude implements Quat {
  x = 0
  y = 0
  z = 0
  w = 1
}

class Direction implements Vec3 {
  x = 0
  y = 0
  z = -1
}

export interface TravelRuntime {
  readonly configs: TravelConfigs
  /** The layout the per-system parts were built for (null between systems). */
  layout: SystemLayout | null
  massLock: MassLockInput | null
  readonly hit: MassLockHit
  /** The detached ship's float64 kinematics (copied from and to `Pos64` around each detached tick). */
  readonly ship: DetachedShip
  /** The nav target's position and shape (refreshed on target, route and system changes). */
  readonly target: CruiseTarget
  readonly tug: TugState
  readonly gate: TransitGateOut
  readonly disc: DiscCrossingOut
  /** Scratch attitude, nose and cruise heading. */
  readonly rot: Quat
  readonly nose: Vec3
  readonly heading: Vec3
  /** The lane ride's direction of travel (the lane direction × `laneSign`). */
  readonly laneDir: Vec3
  /** Lane-ring states by `order` for the lane being entered (sized per system). */
  readonly laneUp: boolean[]
  /** +1 riding toward the lane's `to` end, −1 toward `from`. */
  laneSign: number
  /** `sim.time` of the last system entry and interdiction (−Infinity: never). */
  arrivedAt: number
  lastInterdictAt: number
  /** Seconds since the last interdiction roll. */
  rollTimer: number
  /** The interdictor group rolled at the start of the current tug. */
  interdictors: readonly SpawnShipSpec[] | null
  readonly transit: TransitRun
}

export function createTravelRuntime(configs: TravelConfigs): TravelRuntime {
  return {
    configs,
    layout: null,
    massLock: null,
    hit: createMassLockHit(),
    ship: createDetachedShip(),
    target: createCruiseTarget(),
    tug: createTug(),
    gate: createTransitGate(),
    disc: createDiscCrossing(),
    rot: new Attitude(),
    nose: new Direction(),
    heading: new Direction(),
    laneDir: new Direction(),
    laneUp: [],
    laneSign: 1,
    arrivedAt: -Infinity,
    lastInterdictAt: -Infinity,
    rollTimer: 0,
    interdictors: null,
    transit: { relayId: '', originId: '', serial: 0, readyAt: 0, failed: false, fallbackStarted: false, overrun: false },
  }
}
